import * as angular from 'angular';
import * as _ from 'lodash';

import { guidEmpty } from '../../common';
import { Dictionary, Qualifier, VariableSubType, VariableType } from '../../models';
import { IVariableService } from '../../services';
import { ExtendedVariableType } from './variable.service';

export interface IVariableCascadingService {
    getPromise(): angular.IPromise<any>;
    getVariable(guid: string): VariableType;
    variables(): ExtendedVariableType[];
    variableSubTypes(variableTypeGuid: string): any[];
    variableUnits(variableTypeGuid: string): any[];
    variableQualifiers(variableTypeGuid: string): any[];
    getVariableName(variableTypeGuid: string): string;
    getVariableSubTypeName(variableTypeGuid: string, variableSubtypeGuid: string): string;
    getVariableQualifierName(variableTypeGuid: string, qualifierGuid: string): string;
    getVariableUnitName(variableTypeGuid: string, unitGuid: string): string;
    checkQualifier(variableTypeGuid: string, $data): string | void;
    checkSubType(variableTypeGuid: string, $data): string | void;
    checkUnit(variableTypeGuid: string, $data): string | void;
    // Use these methods instead of method without FromDictionary suffix.
    getVariableNameFromDictionary(variableTypeGuid: string, variableTypesDictionary: Dictionary<VariableType>): string;
    getVariableQualifierNameFromDictionary(
        variableTypeGuid: string,
        qualifierGuid: string,
        variableTypesDictionary: Dictionary<VariableType>
    ): string;
    getVariableSubTypeNameFromDictionary(variableTypeGuid: string,
        variableSubtypeGuid: string,
        variableTypesDictionary: Dictionary<VariableType>,
        variableSubTypesDictionary: Dictionary<VariableSubType>
    ): string;
    getVariableUnitNameFromDictionary(variableTypeGuid: string, unitGuid: string, variableTypesDictionary: Dictionary<VariableType>): string;
}

export class VariableCascadingService implements IVariableCascadingService {
    static serviceId = 'variableCascadingService';
    static $inject = ['variableService'];

    private _variables: ExtendedVariableType[];
    private _promises: angular.IPromise<ExtendedVariableType[]>;

    constructor(private _variableService: IVariableService) {
        this._promises = this._variableService.getAllExtended();
        this._promises.then(data => this._variables = data);
    }

    variables() {
        return this._variables;
    }

    variableSubTypes(variableTypeGuid: string) {
        const variable = _.find(this._variables, (v: any) => v.guid === variableTypeGuid);
        let subtypes = [];
        if (variable) {
            subtypes = variable.subtypes;
        }
        return subtypes;
    }

    variableUnits(variableTypeGuid: string) {
        const variable = this.getVariable(variableTypeGuid);
        if (variable) {
            return variable.unitCollection;
        } else {
            return [];
        }
    }

    variableQualifiers(variableTypeGuid: string) {
        const variable = this.getVariable(variableTypeGuid);
        return variable ? variable.qualifiers : [];
    }

    getVariableName(variableTypeGuid): string {
        let name = '';
        if (variableTypeGuid && variableTypeGuid !== guidEmpty) {
            const variable = this.getVariable(variableTypeGuid);
            if (variable) {
                name = variable.name;
            }
        }

        return name;
    }

    getVariableNameFromDictionary(variableTypeGuid, variableTypesDictionary: Dictionary<VariableType>): string {
        let name = '';
        if (variableTypeGuid && variableTypeGuid !== guidEmpty && variableTypeGuid in variableTypesDictionary) {
            const variable = variableTypesDictionary[variableTypeGuid];
            if (variable) {
                name = variable.name;
            }
        }

        return name;
    }

    getVariableSubTypeName(variableTypeGuid, variableSubtypeGuid): string {
        let name = '';
        if (variableSubtypeGuid !== guidEmpty) {
            const variable: any = _.find(this._variables, v => v.guid === variableTypeGuid);
            if (variable) {
                const subType: any = _.find(variable.subtypes, (s: any) => s.guid === variableSubtypeGuid);
                if (subType) {
                    name = subType.name;
                }
            }
        }
        return name;
    }

    getVariableSubTypeNameFromDictionary(variableTypeGuid: string, variableSubtypeGuid: string, variableTypesDictionary: Dictionary<VariableType>, variableSubTypesDictionary: Dictionary<VariableSubType>): string {
        let name = '';
        if (variableSubtypeGuid !== guidEmpty && variableSubtypeGuid in variableSubTypesDictionary) {
            const variable = variableTypesDictionary[variableTypeGuid];
            if (variable) {
                const subTypeGuid: any = _.find(variable.variableSubTypeGuidCollection, (guid: string) => guid === variableSubtypeGuid);
                if (subTypeGuid && subTypeGuid in variableSubTypesDictionary) {
                    name = variableSubTypesDictionary[subTypeGuid].name;
                }
            }
        }
        return name;
    }

    getVariableQualifierName(variableTypeGuid, qualifierGuid): string {
        let qualifier;
        const variable = this.getVariable(variableTypeGuid);
        if (variable) {
            qualifier = _.find(variable.qualifiers, (q: any) => q.guid === qualifierGuid);
        }
        return (qualifier) ? qualifier.name : '';
    }

    getVariableQualifierNameFromDictionary(variableTypeGuid: string, qualifierGuid: string, variableTypesDictionary: Dictionary<VariableType>): string {
        let qualifier;
        const variable = variableTypesDictionary[variableTypeGuid];
        if (variable) {
            qualifier = _.find(variable.qualifierCollection, (q: Qualifier) => q.guid === qualifierGuid);
        }
        return (qualifier) ? qualifier.name : '';
    }

    getVariableUnitName(variableTypeGuid: string, unitGuid: string): string {
        let unit;
        const variable = this.getVariable(variableTypeGuid);
        if (variable) {
            unit = _.find(variable.unitCollection, (q: any) => q.guid === unitGuid);
        }
        return (unit) ? unit.name : '';
    }

    getVariableUnitNameFromDictionary(variableTypeGuid: string, unitGuid: string, variableTypesDictionary: Dictionary<VariableType>): string {
        let unit;
        const variable = variableTypesDictionary[variableTypeGuid];
        if (variable) {
            unit = _.find(variable.unitCollection, (q: any) => q.guid === unitGuid);
        }
        return (unit) ? unit.name : '';
    }

    checkQualifier(variableTypeGuid, $data): string | void {
        return this.checkVariableProperty(variableTypeGuid, $data, 'qualifiers', 'Qualifier');
    }
    checkSubType(variableTypeGuid, $data): string | void {
        return this.checkVariableProperty(variableTypeGuid, $data, 'subtypes', 'Subtype');
    }

    checkUnit(variableTypeGuid, $data): string | void {
        return this.checkVariableProperty(variableTypeGuid, $data, 'unitCollection', 'Unit');
    }

    private checkVariableProperty(variableTypeGuid, $data, propertyName: string, messageContent: string) {
        let message: string;
        if (variableTypeGuid !== guidEmpty) {
            const variable = this.getVariable(variableTypeGuid);
            if (variable) {
                if (variable[propertyName] && variable[propertyName].length > 0) {
                    if (!$data || $data === guidEmpty) {
                        message = `${messageContent} is required for variable of type ${variable.name}`;
                    }
                }
            }
        }
        return message;
    }

    getVariable(guid: string): any {
        const result = _.find(this._variables, (v: any) => v.guid === guid);
        return result;
    }

    getPromise(): angular.IPromise<any> {
        return this._promises;
    }
}